import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
} from "chart.js";
import { ErrorMessage } from "@/components/ui/error-message";
import { SkeletonCard } from "@/components/ui/skeleton";
import { formatPoints, parseApiFloat } from "@/lib/utils/format";
import { t } from "@/lib/i18n/messages";
import { cn } from "@/lib/utils";
import type { Locale } from "@/types/auth";
import type { WalletBalance, TransactionType } from "@/types/wallet";

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Filler);

interface DashboardStatsProps {
  locale: Locale;
  data: WalletBalance | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  onFilterByType?: (type: TransactionType | undefined) => void;
}

// ── Sparkline helpers ─────────────────────────────────────────────────────────

const MONTH_ORDER = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function buildSparklinePoints(
  monthlyBreakdown: WalletBalance["overview"]["monthly_breakdown"],
  field: "earnings" | "spendings" | "net",
): number[] {
  const entries = MONTH_ORDER.map((m) => {
    const key = m as keyof typeof monthlyBreakdown;
    const val = monthlyBreakdown[key];
    return val ? parseApiFloat(val[field]) || 0 : 0;
  });
  // Return last 7 non-zero months or last 7 values
  return entries.slice(-7);
}

function computeChange(points: number[]): number {
  if (points.length < 2) return 0;
  const first = points[0] ?? 0;
  const last = points[points.length - 1] ?? 0;
  if (first === 0) return last > 0 ? 100 : 0;
  return Math.round(((last - first) / first) * 1000) / 10;
}

interface SparklineProps {
  points: number[];
  color: string;
}

function Sparkline({ points, color }: SparklineProps) {
  const chartData = {
    labels: points.map(() => ""),
    datasets: [
      {
        data: points,
        borderColor: color,
        borderWidth: 1.5,
        fill: true,
        backgroundColor: `${color}18`,
        pointRadius: 0,
        tension: 0.4,
      },
    ],
  };

  const options = {
    responsive: false,
    animation: false as const,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    scales: {
      x: { display: false },
      y: { display: false },
    },
  };

  return <Line data={chartData} options={options} width={80} height={40} />;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function DashboardStats({
  locale,
  data,
  loading,
  error,
  refetch,
  onFilterByType,
}: DashboardStatsProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        title={t(locale, "errorLoadingData")}
        message={error}
        onRetry={refetch}
        retryLabel={t(locale, "retry")}
      />
    );
  }

  if (!data) return null;

  // Build sparkline points from monthly earnings breakdown (same trend for all cards)
  const sparkPoints = buildSparklinePoints(
    data.overview.monthly_breakdown,
    "earnings",
  );
  const spendPoints = buildSparklinePoints(
    data.overview.monthly_breakdown,
    "spendings",
  );

  const ncChange = computeChange(sparkPoints);
  const gpChange = computeChange(sparkPoints);
  const spChange = computeChange(spendPoints);
  const cpChange = computeChange(spendPoints);

  const isClickable = Boolean(onFilterByType);

  const cards = [
    {
      label: t(locale, "nationalCurrency"),
      value: formatPoints(data.n_c_balance, locale),
      icon: "account_balance_wallet",
      iconColor: "text-primary",
      iconBg: "bg-primary/10",
      ariaLabel: `${t(locale, "nationalCurrency")}: ${formatPoints(data.n_c_balance, locale)}`,
      sparkColor: "#005AFF",
      points: sparkPoints,
      change: ncChange,
      filterType: "NcTransfer" as TransactionType,
    },
    {
      label: t(locale, "greenPoints"),
      value: formatPoints(data.g_points, locale),
      icon: "eco",
      iconColor: "text-success dark:text-success",
      iconBg: "bg-success/10",
      ariaLabel: `${t(locale, "greenPoints")}: ${formatPoints(data.g_points, locale)}`,
      sparkColor: "#22C55E",
      points: sparkPoints,
      change: gpChange,
      filterType: "GpEarn" as TransactionType,
    },
    {
      label: t(locale, "smartPoints"),
      value: formatPoints(data.s_points, locale),
      icon: "groups",
      iconColor: "text-info dark:text-info",
      iconBg: "bg-info/10",
      ariaLabel: `${t(locale, "smartPoints")}: ${formatPoints(data.s_points, locale)}`,
      sparkColor: "#3B82F6",
      points: spendPoints,
      change: spChange,
      filterType: "SpEarn" as TransactionType,
    },
    {
      label: t(locale, "communityPoints"),
      value: formatPoints(data.c_points, locale),
      icon: "group",
      iconColor: "text-purple-500 dark:text-purple-400",
      iconBg: "bg-purple-500/10",
      ariaLabel: `${t(locale, "communityPoints")}: ${formatPoints(data.c_points, locale)}`,
      sparkColor: "#A855F7",
      points: spendPoints,
      change: cpChange,
      filterType: "CpEarn" as TransactionType,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
      {cards.map((card, index) => (
        <div
          key={index}
          className={cn(
            "bg-card p-5 md:p-6 rounded-lg shadow-sm border border-border",
            "flex flex-col gap-3",
            "transition-all duration-base",
            "hover:shadow-md hover:border-border-hover",
            isClickable && "cursor-pointer hover:scale-[1.02]",
          )}
          role={isClickable ? "button" : "article"}
          aria-label={card.ariaLabel}
          tabIndex={isClickable ? 0 : undefined}
          onClick={
            isClickable ? () => onFilterByType?.(card.filterType) : undefined
          }
          onKeyDown={
            isClickable
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    onFilterByType?.(card.filterType);
                  }
                }
              : undefined
          }
        >
          {/* Top row: label + icon */}
          <div className="flex justify-between items-start">
            <p className="text-foreground-secondary font-semibold text-md truncate">
              {card.label}
            </p>
            <div
              className={cn(
                "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ms-2",
                card.iconBg,
              )}
              aria-hidden="true"
            >
              <span
                className={cn(
                  "material-icons-outlined text-base",
                  card.iconColor,
                )}
              >
                {card.icon}
              </span>
            </div>
          </div>

          {/* Value + % change badge */}
          <div className="flex items-end gap-2">
            <h3 className="text-2xl font-bold text-foreground tabular-nums">
              {card.value}
            </h3>
            <span
              className={cn(
                "text-xs font-medium px-1.5 py-0.5 rounded mb-0.5",
                card.change >= 0
                  ? "text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/30"
                  : "text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/30",
              )}
            >
              {card.change >= 0 ? "+" : ""}
              {card.change}%
            </span>
          </div>

          {/* Sparkline 
          <div aria-hidden="true">
            <Sparkline points={card.points} color={card.sparkColor} />
          </div>*/}
        </div>
      ))}
    </div>
  );
}
